/**
 * The pay calendar — pay groups, the default rules that generate their monthly cycles, and the
 * persisted cycles HR maintains through the year.
 *
 * TWO ROLES, TWO PERMISSIONS. `payroll.paygroup:manage` (owner) creates groups and changes their
 * default rules; `payroll.calendar:maintain` (HR / payroll admin) edits one cycle's dates. Changing
 * a single month never rewrites the group's rules or any other month.
 *
 * TIME. Every date here is a calendar date in the legal entity's timezone, except `cutoffAt`, which
 * is the instant the cut-off happens (invariant 3). Fiscal years start in
 * `legalEntity.fiscalYearStartMonth` and are named by the calendar year they start in.
 *
 * STATE IS NEVER INFERRED. A cycle is `processed` only when a payroll run marked it so; a pay date
 * in the past with no run behind it is `awaiting_processing`.
 *
 * NOT HERE: freezing attendance. Saving a calendar never locks attendance, and a pay group reaching
 * its cut-off does not trigger the entity-wide `attendance.freezeCycle` — lock scope per pay group
 * is not defined yet.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { HOLIDAY_ADJUSTMENTS, PAY_CYCLE_STATES, PAY_DAY_RULES } from '../enums.js';
import {
  dateOnlySchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  orgIdSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });

export const payDayRuleSchema = z.enum(PAY_DAY_RULES);
export const holidayAdjustmentSchema = z.enum(HOLIDAY_ADJUSTMENTS);
export const payCycleStateSchema = z.enum(PAY_CYCLE_STATES);

/** 24-hour local time, "HH:MM". */
export const localTimeSchema = z
  .string()
  .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/, 'Must be HH:MM in 24-hour time');

// ---------------------------------------------------------------------------
// Pay groups & rules
// ---------------------------------------------------------------------------

const payGroupRulesFields = {
  /** Day of the month attendance is cut off. 29–31 fall back to the month's last day. */
  cutoffDay: z.number().int().min(1).max(31),
  cutoffTime: localTimeSchema,
  /** Days after the cut-off date processing starts (0 = the same day). */
  processingStartOffsetDays: z.number().int().min(0).max(10),
  processingDays: z.number().int().min(1).max(15),
  payDayRule: payDayRuleSchema,
  /** Required for `fixed_day`, ignored otherwise. */
  payDay: z.number().int().min(1).max(31).nullable(),
  holidayAdjustment: holidayAdjustmentSchema,
};

export const payGroupRulesInputSchema = z
  .object(payGroupRulesFields)
  .refine((rules) => rules.payDayRule !== 'fixed_day' || rules.payDay !== null, {
    message: 'Choose the day of the month for a fixed pay date',
    path: ['payDay'],
  });
export type PayGroupRulesInput = z.infer<typeof payGroupRulesInputSchema>;

export const payGroupRulesSchema = z.object({
  ...payGroupRulesFields,
  versionNo: z.number().int().positive(),
  createdAt: instantSchema,
  createdByName: z.string().nullable(),
});
export type PayGroupRules = z.infer<typeof payGroupRulesSchema>;

export const payGroupSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  legalEntityId: uuidSchema,
  legalEntityName: z.string(),
  code: z.string(),
  name: z.string(),
  frequency: z.literal('monthly'),
  isActive: z.boolean(),
  /** From the legal entity — every date on this group's cycles resolves in it. */
  timezone: z.string(),
  fiscalYearStartMonth: z.number().int().min(1).max(12),
  /** The fiscal year "today" (in `timezone`) falls in. */
  currentFiscalYear: z.number().int(),
  rules: payGroupRulesSchema,
  version: z.number().int().positive(),
});
export type PayGroup = z.infer<typeof payGroupSchema>;

export const createPayGroupBodySchema = z.object({
  legalEntityId: uuidSchema,
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9_-]{1,29}$/, '2–30 characters: letters, digits, hyphens, underscores'),
  name: z.string().trim().min(1).max(120),
  rules: payGroupRulesInputSchema,
});

/**
 * Saves a NEW rules version. `applyToUnmaintainedCycles` regenerates dates for this group's cycles
 * that are not processed, not manually maintained, and not yet past their cut-off — history stays
 * exactly as it was generated.
 */
export const updatePayGroupRulesBodySchema = z.object({
  expectedVersion: z.number().int().positive(),
  name: z.string().trim().min(1).max(120).optional(),
  rules: payGroupRulesInputSchema,
  applyToUnmaintainedCycles: z.boolean().default(true),
});

// ---------------------------------------------------------------------------
// Cycles
// ---------------------------------------------------------------------------

export const payCycleSchema = z.object({
  id: uuidSchema,
  payGroupId: uuidSchema,
  fiscalYear: z.number().int(),
  /** 1–12 in fiscal-year order. */
  cycleNo: z.number().int().min(1).max(12),
  periodStart: dateOnlySchema,
  periodEnd: dateOnlySchema,
  cutoffAt: instantSchema,
  /** `cutoffAt` as a local date and time in the entity's timezone. */
  cutoffDate: dateOnlySchema,
  cutoffTime: localTimeSchema,
  processingStartOn: dateOnlySchema,
  processingEndOn: dateOnlySchema,
  payDate: dateOnlySchema,
  /** Why the generator moved the pay date, e.g. "Moved from 31 Aug (Sunday)". Null if it didn't. */
  payDateAdjustment: z.string().nullable(),
  state: payCycleStateSchema,
  isManuallyMaintained: z.boolean(),
  note: z.string().nullable(),
  ruleVersionNo: z.number().int().positive(),
  processedAt: instantSchema.nullable(),
  version: z.number().int().positive(),
  updatedAt: instantSchema,
});
export type PayCycle = z.infer<typeof payCycleSchema>;

export const listPayCyclesQuerySchema = z.object({
  payGroupId: uuidSchema,
  fiscalYear: z.coerce.number().int().min(2000).max(2100),
});

export const payCycleListSchema = z.object({
  fiscalYear: z.number().int(),
  fiscalYearLabel: z.string(),
  fiscalYearStart: dateOnlySchema,
  fiscalYearEnd: dateOnlySchema,
  timezone: z.string(),
  items: z.array(payCycleSchema),
  /** 12 minus the cycles that exist — generate to fill them. */
  missingCycles: z.number().int().min(0).max(12),
  activeCycleId: uuidSchema.nullable(),
  nextCutoffAt: instantSchema.nullable(),
  processedCycles: z.number().int().nonnegative(),
});
export type PayCycleList = z.infer<typeof payCycleListSchema>;

export const generatePayCyclesBodySchema = z.object({
  fiscalYear: z.number().int().min(2000).max(2100),
});

/**
 * One cycle's dates. Ordering is enforced server-side as well as here: cut-off ≤ processing start ≤
 * processing end ≤ pay date. `expectedVersion` rejects an edit made against a stale copy.
 */
export const maintainPayCycleBodySchema = z
  .object({
    expectedVersion: z.number().int().positive(),
    cutoffDate: dateOnlySchema,
    cutoffTime: localTimeSchema,
    processingStartOn: dateOnlySchema,
    processingEndOn: dateOnlySchema,
    payDate: dateOnlySchema,
    note: z.string().trim().max(200).nullable().optional(),
  })
  .refine((b) => b.cutoffDate <= b.processingStartOn, {
    message: 'Processing cannot start before the attendance cut-off',
    path: ['processingStartOn'],
  })
  .refine((b) => b.processingStartOn <= b.processingEndOn, {
    message: 'Processing must end on or after it starts',
    path: ['processingEndOn'],
  })
  .refine((b) => b.processingEndOn <= b.payDate, {
    message: 'The pay date cannot be before processing ends',
    path: ['payDate'],
  });

export const payCycleHistoryEntrySchema = z.object({
  id: uuidSchema,
  action: z.enum(['created', 'updated', 'deleted']),
  actorName: z.string().nullable(),
  at: instantSchema,
  changes: z.array(
    z.object({ field: z.string(), before: z.string().nullable(), after: z.string().nullable() }),
  ),
});
export type PayCycleHistoryEntry = z.infer<typeof payCycleHistoryEntrySchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

const payGroupParams = z.object({ payGroupId: uuidSchema });
const cycleParams = z.object({ cycleId: uuidSchema });

export const payrollCalendarContract = c.router(
  {
    listPayGroups: {
      method: 'GET',
      path: '/pay-groups',
      query: z.object({ legalEntityId: uuidSchema.optional() }),
      responses: {
        200: z.object({ items: z.array(payGroupSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List pay groups with their current rules',
      metadata: routeMeta('payroll:read'),
    },

    createPayGroup: {
      method: 'POST',
      path: '/pay-groups',
      headers: idempotencyHeadersSchema,
      body: createPayGroupBodySchema,
      responses: {
        201: payGroupSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Create a pay group and its first rules version',
      metadata: routeMeta(['payroll.paygroup:manage', 'payroll:read']),
    },

    updatePayGroupRules: {
      method: 'PUT',
      path: '/pay-groups/:payGroupId/rules',
      pathParams: payGroupParams,
      body: updatePayGroupRulesBodySchema,
      responses: {
        200: payGroupSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: "Save a new version of a pay group's default rules",
      metadata: routeMeta(['payroll.paygroup:manage', 'payroll:read']),
    },

    listCycles: {
      method: 'GET',
      path: '/cycles',
      query: listPayCyclesQuerySchema,
      responses: {
        200: payCycleListSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "A pay group's cycles for one fiscal year",
      metadata: routeMeta('payroll:read'),
    },

    generateCycles: {
      method: 'POST',
      path: '/pay-groups/:payGroupId/cycles/generate',
      pathParams: payGroupParams,
      headers: idempotencyHeadersSchema,
      body: generatePayCyclesBodySchema,
      responses: {
        200: payCycleListSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "Create a fiscal year's missing cycles from the group's current rules",
      metadata: routeMeta(['payroll.calendar:maintain', 'payroll:read']),
    },

    maintainCycle: {
      method: 'PATCH',
      path: '/cycles/:cycleId',
      pathParams: cycleParams,
      body: maintainPayCycleBodySchema,
      responses: {
        200: payCycleSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Stale `expectedVersion`, or the cycle is already processed. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: "Change one cycle's dates",
      metadata: routeMeta(['payroll.calendar:maintain', 'payroll:read']),
    },

    getCycleHistory: {
      method: 'GET',
      path: '/cycles/:cycleId/history',
      pathParams: cycleParams,
      responses: {
        200: z.object({ items: z.array(payCycleHistoryEntrySchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "A cycle's change history from the audit log",
      metadata: routeMeta('payroll:read'),
    },
  },
  { pathPrefix: '/payroll/calendar' },
);
